import { Outlet, useParams } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";

import { ReactComponent as LoadingIcon } from "@assets/svg/loading-spinner.svg";

import NavigationWarningModal from "@components/NavigationWarningModal";
import { RecordingNavWarningModal } from "@components/RecordingDock/RecordingNavWarningModal";
import {
  Banner,
  BannerAction,
  BannerIcon,
  BannerTitle,
} from "@components/ui/banner";
import { Breadcrumbs } from "@components/ui/breadcrumb";
import { Skeleton } from "@components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@components/ui/tabs";

import { CASE_ACCESS_STATE } from "@constants/case-access";

import { useCaseProcessingStatus } from "@hooks/queries/use-case-processing-status";
import { useCapabilities, useMe } from "@hooks/queries/use-me";

import { cn } from "@utils/ui";

import { CaseAccessNotice } from "./CaseAccessNotice";
import { CaseSummaryLauncher } from "./CaseSummary";
import { CASE_TABS } from "./constants";
import { CaseDetailProvider, useCaseDetail } from "./context";
import { useCaseDetailTabs } from "./use-case-detail-tabs";

/**
 * Layout shell for a single case within Report Studio.
 *
 * Renders a two-card header unit — a breadcrumb card (breadcrumbs + a bulleted
 * case-details subtitle) stacked directly on top of a navigation card (pill tab
 * bar on the left, per-tab action buttons on the right). The child route
 * (matched by the active tab) supplies its `rightContent` (and `description`)
 * via the CaseDetail context.
 *
 * Child pages are rendered via `<Outlet />`, keyed by case number (MDRS-155)
 * so every tab fully remounts on a case switch instead of reusing the previous
 * case's instance. Switching cases is a route-param-only change on the same
 * path (e.g. `/report-studio/$id/assessment-prep`), so without the key
 * TanStack Router reuses each tab's component and any per-case local state
 * (selected file, search filters, modal state, etc.) — and the effects tied to
 * it — would leak across the switch, as happened with the Assessment Scribe
 * transcript (MDRS-155). This key makes that reset the default for every tab,
 * current and future, rather than something each page has to opt into.
 *
 * Must be wrapped in `CaseDetailProvider` so that child routes can push
 * dynamic `description` and `rightContent` into the header via context.
 */
const CASE_STATUS_UNAVAILABLE_NOTICE =
  "We couldn't check whether documents are still arriving on this case, so " +
  "the report can't be finalised yet.";

const CASE_PROCESSING_NOTICE =
  "Documents are still being uploaded or processed for this case. The report " +
  "can't be finalised until they've finished.";

const CaseDetailContent = () => {
  const { id: caseNumber } = useParams({ strict: false }) as { id: string };

  // Content set by child routes to customise the header per-tab. `rightContent`
  // renders as the nav card's action buttons.
  const { rightContent } = useCaseDetail();

  // Access levels v1: only show tabs the user has the capability for (a
  // referrer sees just Assessment Prep).
  const { can } = useCapabilities();
  // Referrers get "Request documents" as an inline section on the case detail
  // (below Case Details), not a tab — so hide that tab for them.
  const { data: me } = useMe();
  const isReferrer = me?.user?.user_type === "referrer";
  const visibleTabs = CASE_TABS.filter(
    // `request-documents` is commented out of CASE_TABS until the referrer
    // portal ships, so it's not in the id union today — cast keeps this
    // referrer-hide rule ready for when the tab returns.
    (tab) =>
      can(tab.capability) &&
      !(isReferrer && (tab.id as string) === "request-documents")
  );

  // Tab navigation state derived from the current URL
  const {
    accessState,
    activeTab,
    breadcrumbs,
    caseSubtitle,
    isCaseLoading,
    isCaseFetching,
    isOnOverview,
    handleTabChange,
    refetchCase,
    showWarningModal,
    isRecordingWarning,
    recordingExamineeName,
    handleConfirmNavigation,
    handleCancelNavigation,
  } = useCaseDetailTabs();

  const hasAccess = accessState === CASE_ACCESS_STATE.OK;

  // MDRS-508: documents still arriving on the case — usually an admin's bundle
  // upload, or its summaries generating. Shown on every tab so the doctor knows
  // the case is incomplete; Report Builder also holds Finalise back on it.
  const {
    data: processingStatus,
    isError: isStatusError,
    refetch: refetchProcessingStatus,
  } = useCaseProcessingStatus({
    case_number: caseNumber,
    enabled: hasAccess,
  });
  const isCaseProcessing = hasAccess && !!processingStatus?.is_processing;
  // A failed request isn't "nothing arriving". React Query keeps the last good
  // answer through a failed poll, so this only holds before any answer.
  const isStatusUnavailable = hasAccess && !!isStatusError && !processingStatus;
  const liveNotice = isCaseProcessing
    ? CASE_PROCESSING_NOTICE
    : isStatusUnavailable
      ? CASE_STATUS_UNAVAILABLE_NOTICE
      : "";

  // Left padding is intentionally omitted below: the SideNavigation's `m-2`
  // already supplies the 8px gutter between the nav and this content, so
  // adding `pl-2` here would double it to 16px (design gap is 8px).
  return (
    <div className="grid h-dvh grid-cols-1 grid-rows-[auto_1fr] gap-2 py-2 pr-2">
      <div>
        {/* Breadcrumb card — top of the joined header unit. The skeleton is
            gated on `hasAccess` too: a retry re-enters `isLoading` (query-core
            resets status to "pending" while `data` is undefined), and skeletons
            above a visible access notice read as a half-broken page. */}
        <div
          className={cn(
            "flex flex-col gap-2 border border-border bg-background-white p-4",
            // The nav card below normally supplies the bottom half of the joined
            // header unit. With no access there is no nav card, so this one has
            // to close itself off or it reads as a cut-off box.
            hasAccess ? "rounded-t-xl" : "rounded-xl"
          )}
        >
          {isCaseLoading && hasAccess ? (
            <>
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-4 w-2/3" />
            </>
          ) : (
            <>
              <Breadcrumbs items={breadcrumbs} />
              {caseSubtitle && (
                <p className="text-sm text-foreground-muted">{caseSubtitle}</p>
              )}
            </>
          )}
        </div>

        {/* Navigation card — pill tabs (left) + action buttons (right).
            Shares its top border with the card above to read as one unit.
            Access levels v1: render only the tabs the user can see.
            MDRS-343: dropped entirely once the case is known to be unreadable —
            every tab behind it targets the same refused case, and the action
            buttons would offer operations that can only fail. (The first render
            of a fresh case still mounts the tabs while the fetch is in flight;
            gating on the fetch settling would delay every normal load. Those
            child requests are refused server-side.) */}
        {hasAccess && (
          <div className="flex items-center justify-between gap-4 rounded-b-xl border border-t-0 border-border bg-background-white px-4 py-3">
            <div className="flex flex-col gap-2">
              {isOnOverview && (
                <span className="text-sm font-medium text-foreground">
                  Select a step to begin
                </span>
              )}
              <Tabs value={activeTab} onValueChange={handleTabChange}>
                <TabsList variant="pill">
                  {visibleTabs.map((tab) => (
                    <TabsTrigger
                      key={tab.id}
                      variant="pill"
                      value={tab.id}
                      // text-xs to match the size="sm" action buttons this
                      // nav card renders on the right (e.g. "Upload case
                      // bundle") — Button's "sm" size is text-xs, not text-sm.
                      className="text-xs"
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              {rightContent}
              {/* MDRS-486: the page's primary action, so right-most. The button
                  shows on the case detail page only, but the panel lives in
                  the shell so an open one survives tab switches; keyed by case
                  so a case switch closes it. */}
              <CaseSummaryLauncher
                key={caseNumber}
                caseNumber={caseNumber}
                showButton={isOnOverview}
              />
            </div>
          </div>
        )}

        {isCaseProcessing && (
          // role="none": the always-mounted region below is the live region —
          // a status mounted together with its text often isn't announced.
          <Banner variant="warning" inset className="mt-2" role="none">
            <BannerIcon>
              <LoadingIcon className="animate-spin-ccw" />
            </BannerIcon>
            <BannerTitle>{CASE_PROCESSING_NOTICE}</BannerTitle>
          </Banner>
        )}

        {/* The notice usually appears mid-session (an admin starts an upload
            while the doctor is on the case). Screen readers only announce
            changes to a live region that already exists, so this one stays
            mounted and only its text changes. Visually hidden — sighted users
            get the banner above. */}
        {isStatusUnavailable && (
          <Banner variant="warning" inset className="mt-2" role="none">
            <BannerIcon>
              <AlertTriangle />
            </BannerIcon>
            <BannerTitle>{CASE_STATUS_UNAVAILABLE_NOTICE}</BannerTitle>
            <BannerAction onClick={() => void refetchProcessingStatus()}>
              Retry
            </BannerAction>
          </Banner>
        )}

        <p role="status" className="sr-only">
          {liveNotice}
        </p>
      </div>

      {/* Child route content fills remaining space.
          min-h-0/min-w-0 are required here: this div is a CSS grid item in
          the `1fr` row of the parent grid, and grid items — like flex items —
          default to `min-height: auto` / `min-width: auto`, meaning they'll
          grow to fit their content instead of respecting the track's
          allocated space. Without them, tall or wide content (a long live
          transcript, a wide table row) grows this container past the
          viewport instead of being clipped/scrolled by the descendants that
          expect a real bounded size. */}
      <div className="h-full min-h-0 min-w-0 overflow-hidden">
        {hasAccess ? (
          <Outlet key={caseNumber} />
        ) : (
          <CaseAccessNotice
            state={accessState}
            onRetry={refetchCase}
            isRetrying={isCaseFetching}
          />
        )}
      </div>

      {/* Report-preview unsaved-changes guard. */}
      <NavigationWarningModal
        isOpen={showWarningModal && !isRecordingWarning}
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
      />

      {/* Recording case-boundary guard — a live recording is about to leave
          its case via a breadcrumb link. */}
      <RecordingNavWarningModal
        isOpen={showWarningModal && isRecordingWarning}
        examineeName={recordingExamineeName}
        onStay={handleCancelNavigation}
        onFinishAndLeave={handleConfirmNavigation}
      />
    </div>
  );
};

const ReportStudioCaseDetail = () => {
  return (
    <CaseDetailProvider>
      <CaseDetailContent />
    </CaseDetailProvider>
  );
};

export default ReportStudioCaseDetail;
